import {
  BoxV2 as Box,
  Flex,
  RoleGuard,
  Tooltip,
  useTable,
} from 'portal-commons';
import React, { useState, useEffect, FC, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  faAngleLeft,
  faArrowProgress,
  faShareNodes,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, Container } from '@material-ui/core';

import {
  deactivateCampaign,
  fetchCampaignDetails,
  fetchResellerDetail,
  getBuilderInfo,
  getCampaignOperations,
  getEventHistory,
  getNominationStatusApi,
  listCampaignElectedDcaApi,
  nudgeUpstreamCnp,
  resubmitCampaign,
  updateCampaign,
} from '../../apiServices';
import {
  cancelCampaignMigrations,
  getCnpList,
  getUseCaseGreyOutFields,
  initiateCampaignMigrations,
} from '../../apis';
import {
  CNPDetails,
  CampaignAttributesSection,
  CampaignDetailsBasic,
  CampaignDetailsSection,
  CampaignEditModal,
  CampaignResubmissionModal,
  ConfirmationDialog,
  ElectCNPAlert,
  ElectCNPDialog,
  EventsSection,
  LiftSuspensionModal,
  MaskedContainer,
  MenuButton,
  MnoStatusTableNew,
  NumbersProvisioned,
  OtherResponsiblePartiesSection,
  ReSellerDetails,
  RequestAppealRejectionModal,
  RequestReviewModal,
  SampleMessagesSection,
  SampleMultimediaSection,
  SectionHeader,
  SupportingDocumentSection,
  SuspendModal,
} from '../../components';
import { NUDGE_TYPES } from '../../constants';
import { BulkOperationError } from '../../errors';
import { CampaignAttributeKey } from '../../types';
import { getMigrationAndProvisionalInfo, isFieldGreyOut } from '../../utils';
import { selectDynamicContent } from '../../../CSP-Form/selectors';
import { MAX_EVENT_RECORDS } from '../../../Events/constants';
import { Event } from '../../../Events/types';
import {
  CampaignActionEligibility,
  CampaignDetail,
  CampaignOperation,
  MnoMetadata,
  Reseller,
  campaignController,
  cnpChainController,
  enumController,
} from '@/apis/core';
import {
  MY_CAMPAIGNS_DEFAULT_ENTRY,
  MY_CAMPAIGNS_PATH,
} from '@/constants/paths';
import { Loader } from '@/shared_elements';
import { toastFlashMessage } from '@/utils';
import { SelectItem } from '@/utils/types';

import '@/assets/styles/campaign-detail-module.scss';

const REVIEW_ACTION_VALUE = 0;
const APPEAL_REJECTION_ACTION_VALUE = 1;
const RESUBMIT_ACTION_VALUE = 2;
const DEACTIVATE_ACTION_VALUE = 3;
const SUSPEND_ACTION_VALUE = 4;
const LIFT_SUSPENSION_ACTION_VALUE = 5;

export const MENU_OPTIONS = [
  { label: 'Nudge CNP to Review', value: REVIEW_ACTION_VALUE },
  {
    label: 'Nudge CNP to Appeal Rejection',
    value: APPEAL_REJECTION_ACTION_VALUE,
  },
  { label: 'Resubmit Campaign', value: RESUBMIT_ACTION_VALUE },
  { label: 'Deactivate Campaign', value: DEACTIVATE_ACTION_VALUE },
];

interface RouteParams {
  id: string;
}

interface LocationState {
  goBackPage?: string;
}

const CampaignDetailV2: FC = () => {
  const { id: campaignUid } = useParams<RouteParams>();
  const eventTable = useTable<Event>(
    async (tableFilter) => {
      const response = await getEventHistory({ campaignUid, ...tableFilter });
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      deps: [campaignUid],
      inMemory: true,
      cappingTotal: MAX_EVENT_RECORDS,
    }
  );
  const dynamicContent = useSelector(selectDynamicContent);
  const history = useHistory();
  const location = useLocation<LocationState>();

  const [loader, setLoader] = useState(true);
  const [campaign, setCampaign] = useState<CampaignDetail>();
  const [goBackPage] = useState(
    location.state?.goBackPage ?? MY_CAMPAIGNS_DEFAULT_ENTRY
  );
  const [resellerInfo, setResellerInfo] = useState<Reseller>();
  const [electedDcas, setElectedDcas] = useState<number[]>([]);
  const [electCnpOptions, setElectCnpOptions] = useState<SelectItem[]>([]);
  const [migrateCnpOptions, setMigrateCnpOptions] = useState<SelectItem[]>([]);
  const [nominationInfo, setNominationInfo] = useState<Record<string, unknown>>(
    {}
  );
  const [openAlert, setOpenAlert] = useState(false);
  const [reviewFlag, setReviewFlag] = useState(false);
  const [appealRejectionFlag, setAppealRejectionFlag] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editType, setEditType] = useState('');
  const [editLoader, setEditLoader] = useState(false);
  const [campaignResubmissionModalOpen, setCampaignResubmissionModalOpen] =
    useState(false);
  const [deactivateFlag, setDeactivateFlag] = useState(false);
  const [newMnoTerms, setNewMnoTerms] = useState<Record<string, MnoMetadata>>(
    {}
  );
  const [mnoStatusInfo, setMnoStatusInfo] = useState<CampaignOperation[]>([]);
  const [migrationInfo, setMigrationInfo] = useState<any>(null);
  const [provisional, setProvisional] = useState<boolean | null>(null);
  const [greyOutFields, setGreyOutFields] = useState<string[]>([]);
  const [excludedMnos, setExcludedMnos] = useState<number[]>([]);
  const [campaignActionEligibility, setCampaignActionEligibility] =
    useState<CampaignActionEligibility>();
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [liftSuspensionModalOpen, setLiftSuspensionModalOpen] = useState(false);
  const [suspensionCategoryOptions, setSuspensionCategoryOptions] = useState<
    SelectItem[]
  >([]);
  const [modalLoading, setModalLoading] = useState(false);

  const renewalData = useMemo(
    () =>
      dynamicContent.find(
        (item) =>
          item.lookup_key === 'csp-campaign-dialog-confirm#cancel-auto-renewal'
      ),
    [dynamicContent]
  );
  const deactivateData = useMemo(
    () =>
      dynamicContent.find(
        (item) => item.lookup_key === 'csp-campaign-dialog-confirm#deactivate'
      ),
    [dynamicContent]
  );

  useEffect(() => {
    document.title = 'The Campaign Registry - Campaigns';
    updateSuspensionCategoryOptions();
  }, []);

  useEffect(() => {
    setCampaign(undefined);
    setProvisional(null);
    updateProvisionalFlag(campaignUid);
    updateMnoStatus(campaignUid);
    updateExcludedMnos(campaignUid);
    updateCampaignActionEligibility(campaignUid);
  }, [campaignUid]);

  // When provisional changes, update dependent data
  useEffect(() => {
    if (provisional !== null && campaignUid) {
      updateProvisionalDependentData(campaignUid, provisional);
    }
  }, [campaignUid, provisional]);

  // When campaign changes, update grey-out fields
  useEffect(() => {
    updateGreyOutFields(campaign);
  }, [campaign?.uid]);

  useEffect(() => {
    if (campaign?.brandUid && campaign?.status === 'ACTIVE') {
      getBuilderInfo(campaign.brandUid, campaign.usecase)
        .then((response) => {
          setNewMnoTerms(response.mnoMetadata);
        })
        .catch(() => {
          setNewMnoTerms({});
        });
    }
  }, [campaign?.brandUid, campaign?.status]);

  const updateProvisionalFlag = async (campaignUid: string) => {
    const result = await getMigrationAndProvisionalInfo(campaignUid);
    setMigrationInfo(result.migrationInfo);
    setProvisional(result.provisional);
    return result.provisional as boolean;
  };

  // Functions to update data
  const updateProvisionalDependentData = async (uid: string, prov: boolean) => {
    await Promise.allSettled([
      updateCampaignDetails(uid, prov),
      updateNominationStatus(uid, prov),
      updateElectedDca(uid, prov),
    ]);
  };

  const updateCampaignDetails = async (uid: string, prov: boolean) => {
    const data = await fetchCampaignDetails(uid, { provisional: prov });
    if (data) {
      setCampaign(data.campaign);
      updateCnpOptions(data.campaign?.usecase, prov);
      if (data?.campaign?.resellerUid) {
        const resellerInfo = await fetchResellerDetail(
          data?.campaign?.resellerUid
        );
        setResellerInfo(resellerInfo);
      } else {
        setResellerInfo(undefined);
      }
      setLoader(false);
    }
  };

  const updateCnpOptions = async (usecase: string, prov: boolean) => {
    const soleProprietor = usecase === 'SOLE_PROPRIETOR' ? true : undefined;
    const electCnpParams = {
      cnpMigrationSupported: prov ? true : undefined,
      soleProprietorEnabled: soleProprietor,
    };
    const migrateCnpParams = {
      cnpMigrationSupported: true,
      soleProprietorEnabled: soleProprietor,
    };
    const [electCnps, migrateCnps] = await Promise.all([
      getCnpList(electCnpParams),
      getCnpList(migrateCnpParams),
    ]);
    setElectCnpOptions(
      electCnps.map((cnp) => ({ label: cnp.displayName, value: cnp.uid }))
    );
    setMigrateCnpOptions(
      migrateCnps.map((cnp) => ({
        label: cnp.displayName,
        value: cnp.uid,
      }))
    );
  };

  const updateNominationStatus = async (uid: string, prov: boolean) => {
    const data = await getNominationStatusApi(uid, { provisional: prov });
    if (data) {
      setNominationInfo(data.nominationInfo);
      setOpenAlert(data.openAlert);
    } else {
      setNominationInfo({});
      setOpenAlert(false);
    }
  };

  const updateElectedDca = async (uid: string, prov: boolean) => {
    const dcas = await listCampaignElectedDcaApi(uid, { provisional: prov });
    setElectedDcas(dcas);
  };

  const updateMnoStatus = async (uid: string) => {
    const mnoStatusInfo = await getCampaignOperations(uid);
    setMnoStatusInfo(mnoStatusInfo);
  };

  const updateExcludedMnos = async (uid: string) => {
    const mnoIds = await campaignController.getExcludedMnos(uid);
    setExcludedMnos(mnoIds);
  };

  const handleRemoveMno = ({ id, name }: { id: string; name: string }) => {
    toastFlashMessage(`${name} successfully removed`, 'success');
    updateMnoStatus(campaignUid);
    updateExcludedMnos(campaignUid);
  };

  const updateGreyOutFields = async (campaign?: CampaignDetail) => {
    if (campaign && campaign.status === 'ACTIVE') {
      const { brandUid, usecase } = campaign;
      const response = await getUseCaseGreyOutFields(brandUid, usecase);
      setGreyOutFields(response);
    } else {
      setGreyOutFields([]);
    }
  };

  const updateCampaignActionEligibility = async (campaignUid: string) => {
    const response = await cnpChainController.getCampaignActionEligibility([
      campaignUid,
    ]);
    if (response[campaignUid]) {
      setCampaignActionEligibility(response[campaignUid]);
    } else {
      setCampaignActionEligibility(undefined);
    }
  };

  const updateSuspensionCategoryOptions = async () => {
    const suspensionCategories = await enumController.getSuspensionCategories();
    setSuspensionCategoryOptions(
      suspensionCategories.map((suspensionCategory) => ({
        label: suspensionCategory.name,
        value: suspensionCategory.id,
      }))
    );
  };

  const isGreyOut = (field: string) => isFieldGreyOut(greyOutFields, field);

  const handleGoBack = () => history.push(goBackPage);

  const handleEditClick = (type: string) => {
    setOpenEditModal(true);
    setEditType(type);
  };
  const handleSubmitEdit = async (data: any) => {
    // FIXME: this follows what the old codes do
    // but while updating the campaign the inline fields are not updated (e.g. optinKeywords, optoutKeywords...)
    updateCampaignAndReseller(campaignUid, data);
  };

  const handleInlineUpdate = async (data: any) => {
    if (!campaign) return;
    try {
      await updateCampaign(campaignUid, data);
      setCampaign({
        ...campaign,
        ...data,
      });
      toastFlashMessage('Campaign updated successfully', 'success');
    } catch (e) {
      // Reset with campaign copy so keyword and message attributes in popup get reverted
      setCampaign({ ...campaign });
    }
  };

  const handleDeactivateCampaign = async () => {
    setEditLoader(true);
    try {
      const newCampaign = await deactivateCampaign(campaignUid, campaign);
      toastFlashMessage('Campaign deactivated successfully', 'success');
      setCampaign(newCampaign);
      setDeactivateFlag(false);
    } catch (e) {
    } finally {
      setEditLoader(false);
    }
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleCnpChange = (success: boolean) => {
    if (success && provisional !== null) {
      updateNominationStatus(campaignUid, provisional);
      updateElectedDca(campaignUid, provisional);
    }
  };

  const handleActionSelect = (value: number) => {
    switch (value) {
      case REVIEW_ACTION_VALUE:
        setReviewFlag(true);
        break;
      case APPEAL_REJECTION_ACTION_VALUE:
        setAppealRejectionFlag(true);
        break;
      case RESUBMIT_ACTION_VALUE:
        setCampaignResubmissionModalOpen(true);
        break;
      case DEACTIVATE_ACTION_VALUE:
        setDeactivateFlag(true);
        break;
      case SUSPEND_ACTION_VALUE:
        setSuspendModalOpen(true);
        break;
      case LIFT_SUSPENSION_ACTION_VALUE:
        setLiftSuspensionModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleReviewRequest = async (message: string) => {
    try {
      await nudgeUpstreamCnp(campaignUid, NUDGE_TYPES.REVIEW, message);
      toastFlashMessage('Nudge successfully sent.', 'success');
    } catch (e) {}
  };

  const handleAppealRejectionRequest = async (message: string) => {
    try {
      await nudgeUpstreamCnp(
        campaignUid,
        NUDGE_TYPES.APPEAL_REJECTION,
        message
      );
      toastFlashMessage('Notification successfully sent.', 'success');
    } catch (e) {}
  };

  const handleCampaignResubmission = async (newMnos: string[] = []) => {
    try {
      await resubmitCampaign(campaignUid, newMnos);
      toastFlashMessage('Campaign resubmission successful', 'success');
      updateMnoStatus(campaignUid);
    } catch (e) {}
  };

  const updateCampaignAndReseller = async (cUid: string, data: any) => {
    setEditLoader(true);
    try {
      const newCampaign = await updateCampaign(cUid, data);

      toastFlashMessage('Campaign updated successfully', 'success');
      newCampaign.brandName = campaign?.brandName || '';
      setCampaign(newCampaign);

      if (newCampaign.resellerUid) {
        const resellerInfo = await fetchResellerDetail(newCampaign.resellerUid);
        setResellerInfo(resellerInfo);
      } else {
        setResellerInfo(undefined);
      }
    } catch (e) {
    } finally {
      setEditLoader(false);
      setOpenEditModal(false);
    }
  };

  const handleUpdateReseller = (resellerUid: string) => {
    const data = { ...campaign, resellerUid };
    updateCampaignAndReseller(campaignUid, data);
  };

  const handleAttributeChange = async (
    value: boolean,
    key: CampaignAttributeKey,
    label: string
  ) => {
    if (campaign && campaign[key] !== value) {
      setCampaign({
        ...campaign,
        [key]: value,
      });
      try {
        await updateCampaign(campaignUid, { [key]: value });
        toastFlashMessage(`${label} attribute updated`, 'success');
      } catch (e) {
        setCampaign(campaign);
      }
    }
  };

  const handleMigration = async (value: any) => {
    setEditLoader(true);
    try {
      const result = await initiateCampaignMigrations(value, [campaignUid]);
      if (result) {
        toastFlashMessage('Success: Campaign migrated', 'success');
        const newProv = await updateProvisionalFlag(campaignUid);
        await updateProvisionalDependentData(campaignUid, newProv);
      }
    } catch (e: any) {
      let errorMessage = 'Action failed. Cannot migrate campaign: ';
      Object.entries(e.details || {}).forEach(([_key, errorDetail]: any) => {
        errorMessage += `${errorDetail.description}`;
      });
      toastFlashMessage(errorMessage, 'error');
    } finally {
      setEditLoader(false);
    }
  };

  const handleCancelMigration = async (explanation: string) => {
    setEditLoader(true);
    try {
      const result = await cancelCampaignMigrations([campaignUid], explanation);
      if (result) {
        toastFlashMessage('Success: Campaign migration canceled', 'success');
        const newProv = await updateProvisionalFlag(campaignUid);
        await updateProvisionalDependentData(campaignUid, newProv);
      }
    } catch (e: any) {
      let errorMessage = 'Action failed. Cannot cancel campaign migration: ';
      typeof e.details === 'object' &&
        Object.entries(e.details).forEach(([_key, errorDetail]: any) => {
          errorMessage += `${errorDetail.description}`;
        });
      toastFlashMessage(errorMessage, 'error');
    } finally {
      setEditLoader(false);
    }
  };

  const handleSuspend = async (
    suspensionCategory: string,
    explanation: string
  ) => {
    setModalLoading(true);
    try {
      const response = await campaignController.bulkSuspendCampaigns(
        [campaignUid],
        suspensionCategory,
        explanation
      );
      if (response) {
        toastFlashMessage('Campaign successfully suspended', 'success');
        updateCampaignActionEligibility(campaignUid);
        updateMnoStatus(campaignUid);
        setSuspendModalOpen(false);
      }
    } catch (error) {
      if (error instanceof BulkOperationError) {
        Object.entries(error.details).forEach(([key, value]) => {
          toastFlashMessage(
            `Action Failed. Cannot suspend campaign ${key}: ${value.description}`,
            'error'
          );
        });
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleLiftSuspension = async (explanation: string) => {
    setModalLoading(true);
    try {
      const response = await campaignController.bulkLiftSuspensions(
        [campaignUid],
        explanation
      );
      if (response) {
        toastFlashMessage('Campaign suspension successfully lifted', 'success');
        updateCampaignActionEligibility(campaignUid);
        updateMnoStatus(campaignUid);
        setLiftSuspensionModalOpen(false);
      }
    } catch (error) {
      if (error instanceof BulkOperationError) {
        Object.entries(error.details).forEach(([key, value]) => {
          toastFlashMessage(
            `Action Failed. Cannot lift suspension for campaign ${key}: ${value.description}`,
            'error'
          );
        });
      }
    } finally {
      setModalLoading(false);
    }
  };

  const expiredCampaign = campaign?.status === 'EXPIRED';

  return (
    <section className="campaign-detail-section" data-testid="campaignDetail">
      {loader || !campaign ? (
        <Loader />
      ) : (
        <Container maxWidth={false} className="campaign-detail-container">
          <Grid container className="top-blk">
            <Grid item xs={6} className="back-btn">
              <a
                onClick={handleGoBack}
                className="paragraph"
                data-testid="campaignDetailBackButton"
              >
                <Flex>
                  <Box sx={{ mr: 'xs' }}>
                    <FontAwesomeIcon icon={faAngleLeft} color="#68737A" />
                  </Box>
                  {goBackPage.startsWith(MY_CAMPAIGNS_PATH)
                    ? 'back to all campaigns'
                    : 'back'}
                </Flex>
              </a>
            </Grid>
            <Grid item xs={6} className="button-wrapper">
              <RoleGuard feature="campaignDetail.viewMoreActions">
                <p>
                  <MenuButton
                    style={{ float: 'right' }}
                    disabled={expiredCampaign}
                    options={[
                      ...MENU_OPTIONS,
                      ...(campaignActionEligibility?.suspendCampaign
                        ? [
                            {
                              label: 'Suspend Campaign',
                              value: SUSPEND_ACTION_VALUE,
                            },
                          ]
                        : []),
                      ...(campaignActionEligibility?.unsuspendCampaign
                        ? [
                            {
                              label: 'Lift Suspension',
                              value: LIFT_SUSPENSION_ACTION_VALUE,
                            },
                          ]
                        : []),
                    ]}
                    onSelect={handleActionSelect}
                  />
                  <Tooltip
                    title={
                      <div>
                        <div>
                          <b>Review</b>: Use this to remind your CNP to review
                          the campaign and/or take action.
                        </div>
                        <br />
                        <div>
                          <b>Appeal Rejection</b>: Use this after you have
                          edited and resubmitted a campaign to appeal the
                          rejection by your CNP/DCA.
                        </div>
                        <br />
                        <div>
                          <b>Resubmit Campaign</b>: Use this to resubmit a
                          campaign to pick up the highest carrier terms or
                          vetting results. This does not trigger a review by
                          your CNP or DCA partner. Do not use this to resubmit a
                          campaign that has been rejected by your CNP or DCA.
                        </div>
                        <br />
                        <div>
                          <b>Deactivate</b>: The Campaign will be immediately
                          deactivated on all MNOs.
                        </div>
                      </div>
                    }
                  />
                </p>
              </RoleGuard>
            </Grid>
          </Grid>
          <Grid container spacing={0}>
            <Grid item xs={12}>
              <div className="title" data-testid="campaignDetailCampaignId">
                <h3 className="heading1">10DLC Campaign ID: {campaign?.uid}</h3>
              </div>
            </Grid>
            <Grid item xs={12}>
              <CampaignDetailsBasic
                campaign={campaign}
                renewalData={renewalData}
                migrationInfo={migrationInfo}
                handleSubmitEdit={handleSubmitEdit}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <div
                className="mno details"
                data-testid="campaignDetailCarrierStatus"
              >
                <SectionHeader icon={faShareNodes} title="Carrier Status" />
                <MnoStatusTableNew
                  campaignUid={campaignUid}
                  mnoStatusInfo={mnoStatusInfo}
                  electedDcas={electedDcas}
                  excludedMnos={excludedMnos}
                  onRemoveMno={handleRemoveMno}
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <NumbersProvisioned campaignUid={campaign.uid} />
            </Grid>
            <CampaignAttributesSection
              readonly={expiredCampaign}
              campaign={campaign}
              disabledFields={greyOutFields}
              onAttributeChange={handleAttributeChange}
              onDataChange={handleInlineUpdate}
            />
            <MaskedContainer
              masked={[
                isGreyOut('description'),
                isGreyOut('privacyPolicyLink'),
                isGreyOut('termsAndConditionsLink'),
                isGreyOut('sampleMessage'),
              ].every((v) => v)}
            >
              <CampaignDetailsSection
                disabled={expiredCampaign}
                campaign={campaign}
                onChange={(data: any) =>
                  setCampaign((prev) => ({ ...prev, ...data }))
                }
                greyOutFields={greyOutFields}
              />
            </MaskedContainer>
            <MaskedContainer masked={isGreyOut('supportingDocumentUuid')}>
              <SupportingDocumentSection
                readonly={expiredCampaign}
                campaignUid={campaignUid}
              />
            </MaskedContainer>
            <MaskedContainer
              className="sample-messages details"
              masked={isGreyOut('sampleMessage')}
            >
              <SampleMessagesSection
                readonly={expiredCampaign}
                campaign={campaign}
                onEdit={() => handleEditClick('messages')}
              />
            </MaskedContainer>
            <MaskedContainer
              className="sample-messages details"
              data-testid="campaignDetailSampleMultimedia"
              masked={isGreyOut('mmsMediaUuid')}
            >
              <SampleMultimediaSection
                readonly={expiredCampaign}
                campaignUid={campaignUid}
              />
            </MaskedContainer>
            <OtherResponsiblePartiesSection
              loading={editLoader}
              provisional={provisional}
              campaign={campaign}
              nominationInfo={nominationInfo}
              electCnpOptions={electCnpOptions}
              migrationCnpOptions={migrateCnpOptions}
              reseller={resellerInfo}
              onCnpChange={handleCnpChange}
              onMigrationApply={handleMigration}
              onMigrationCancel={handleCancelMigration}
              onResellerChange={handleUpdateReseller}
            />
            <EventsSection
              loading={eventTable.loading}
              events={eventTable.tableData}
              pagination={eventTable.pagination}
              onPageChange={eventTable.handleChangePage}
            />
          </Grid>

          <CampaignEditModal
            open={openEditModal}
            handleClose={() => setOpenEditModal(false)}
            type={editType}
            campaignInfo={campaign}
            loader={editLoader}
            handleSubmitEdit={handleSubmitEdit}
          />
          <ConfirmationDialog
            open={deactivateFlag}
            type="deactivate"
            handleCancel={() => setDeactivateFlag(false)}
            handleSuccess={handleDeactivateCampaign}
            loader={editLoader}
            text={deactivateData?.text ?? ''}
          />
          <ElectCNPAlert handleClose={handleCloseAlert} open={openAlert} />
          <RequestReviewModal
            open={reviewFlag}
            onClose={() => setReviewFlag(false)}
            onSend={handleReviewRequest}
          />
          <RequestAppealRejectionModal
            open={appealRejectionFlag}
            onClose={() => setAppealRejectionFlag(false)}
            onSend={handleAppealRejectionRequest}
          />
          <CampaignResubmissionModal
            open={campaignResubmissionModalOpen}
            mnoStatus={mnoStatusInfo}
            newMnoTerms={newMnoTerms}
            excludedMnos={excludedMnos}
            onClose={() => setCampaignResubmissionModalOpen(false)}
            onSubmit={(mnos: string[]) => {
              handleCampaignResubmission(mnos);
              setCampaignResubmissionModalOpen(false);
            }}
          />
          <SuspendModal
            open={suspendModalOpen}
            disabled={modalLoading}
            campaign={campaign}
            suspensionCategoryOptions={suspensionCategoryOptions}
            onClose={() => setSuspendModalOpen(false)}
            onSubmit={handleSuspend}
          />
          <LiftSuspensionModal
            open={liftSuspensionModalOpen}
            disabled={modalLoading}
            campaign={campaign}
            onClose={() => setLiftSuspensionModalOpen(false)}
            onSubmit={handleLiftSuspension}
          />
        </Container>
      )}
    </section>
  );
};

export default CampaignDetailV2;
